import React from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { Box, Flex, Text, Tooltip } from '@mantine/core';
import { VIS_NEUTRAL_COLOR } from 'visyn_core/vis';

import type { NetworkMapClonotype, NetworkMapClonotypeNode } from './networkMap.utils';
import { VIS_UNSELECTED_COLOR, getNodeColor, getNodeLegendLabel, getNodeSize } from './networkMap.utils';
import { selectColorMapBy } from '../store/slices/clonotypes.slice';
import type { AppState } from '../store/store';

/**
 * MiniNetworkGraphSvg — a lightweight, static SVG rendering of a clonotype's network
 * graph for the per-row Network Map column in the AB table( this sped up the performance load of the table)
 */

const EMPTY_CLONOTYPE_IDS: number[] = [];

const MAX_NODE_RADIUS = 11;

interface ClonotypeTooltipState {
  type: 'clonotype';
  clonotypeId: number;
  numberOfNodes: number;
  clonotypeSize: number;
  vgSegment: string | undefined;
  cdrh3AaLen: number | undefined;
}

interface NodeTooltipState {
  type: 'node';
  clonotypeId: number;
  numberOfCells: number;
  vgSegment: string | undefined;
  cdrh3AaLen: number | undefined;
  cdr3Sequence: string;
  color: string;
  legendLabel: string | null;
}

export function MiniNetworkGraphSvg({ clonotype, width, height }: { clonotype: NetworkMapClonotype; width: number; height: number }) {
  const params = useParams<{ id: string }>();
  const experimentId = params.id as string;
  const { clonotypeId } = clonotype;

  const selectedClonotypes = useSelector((state: AppState) => state.clonotypes[experimentId]?.selectedClonotypes ?? EMPTY_CLONOTYPE_IDS);
  const isSelected = selectedClonotypes.includes(clonotypeId);
  const colorMapBy = useSelector((state: AppState) => selectColorMapBy(state, experimentId));

  const getNodeColorForNode = React.useCallback(
    (node: NetworkMapClonotypeNode) => getNodeColor(node, colorMapBy, clonotype.clonotypeId),
    [clonotype.clonotypeId, colorMapBy],
  );

  const getLegendLabel = React.useCallback(
    (node: NetworkMapClonotypeNode) => getNodeLegendLabel(node, colorMapBy, clonotype.clonotypeId),
    [clonotype.clonotypeId, colorMapBy],
  );

  // Map each node's normalized (x, y) into the SVG pixel box, reserving MAX_NODE_RADIUS
  // of padding so circles never clip at the edges.
  const layout = React.useMemo(() => {
    const xs = clonotype.nodes.map((n) => n.x);
    const ys = clonotype.nodes.map((n) => n.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const rangeX = maxX - minX || 1;
    const rangeY = maxY - minY || 1;
    const pad = MAX_NODE_RADIUS + 2;
    const drawW = Math.max(width - pad * 2, 1);
    const drawH = Math.max(height - pad * 2, 1);
    const positions: Record<string, { cx: number; cy: number }> = {};
    for (const node of clonotype.nodes) {
      positions[node.id] = {
        cx: pad + ((node.x - minX) / rangeX) * drawW,
        cy: pad + ((node.y - minY) / rangeY) * drawH,
      };
    }
    return positions;
  }, [clonotype.nodes, width, height]);

  const { cdrh3AaLen, vGSegment } = React.useMemo(() => {
    const anyCell = clonotype.nodes[0]?.cells[0];
    return {
      cdrh3AaLen: anyCell?.cdrh3AaLen ?? undefined,
      vGSegment: `${anyCell?.irVdj1VCall?.split('*')[0] ?? ''}/${anyCell?.irVdj1JCall}`,
    };
  }, [clonotype.nodes]);

  const clonotypeTooltip: ClonotypeTooltipState = React.useMemo(
    () => ({
      type: 'clonotype',
      clonotypeId: clonotype.clonotypeId,
      numberOfNodes: clonotype.nodes.length,
      clonotypeSize: clonotype.nodes.reduce((acc, node) => acc + node.cells.length, 0),
      vgSegment: vGSegment,
      cdrh3AaLen,
    }),
    [cdrh3AaLen, clonotype.clonotypeId, clonotype.nodes, vGSegment],
  );

  const [tooltipState, setTooltipState] = React.useState<ClonotypeTooltipState | NodeTooltipState>(clonotypeTooltip);

  // Keep the default (clonotype-level) tooltip in sync if the clonotype changes.
  React.useEffect(() => {
    setTooltipState(clonotypeTooltip);
  }, [clonotypeTooltip]);

  const linkOpacity = clonotype.links.length > 300 ? 0.05 : clonotype.links.length > 200 ? 0.1 : 0.2;
  const linkColor = isSelected ? 'rgb(150,150,150)' : VIS_UNSELECTED_COLOR;

  const tooltipContent = React.useMemo(() => {
    if (tooltipState.type === 'clonotype') {
      return (
        <Box>
          <Text size="sm">Clonotype ID: {tooltipState.clonotypeId}</Text>
          <Text size="sm">Number of nodes: {tooltipState.numberOfNodes}</Text>
          <Text size="sm">Clonotype size: {tooltipState.clonotypeSize}</Text>
          <Text size="sm">V-J gene segment: {tooltipState.vgSegment}</Text>
          <Text size="sm">CDR3 length: {tooltipState.cdrh3AaLen}</Text>
        </Box>
      );
    }
    return (
      <Box>
        <Text size="sm">Clonotype ID: {tooltipState.clonotypeId}</Text>
        <Text size="sm">Number of cells: {tooltipState.numberOfCells}</Text>
        <Text size="sm">V-J gene segment: {tooltipState.vgSegment}</Text>
        <Text size="sm">CDR3 length: {tooltipState.cdrh3AaLen}</Text>
        <Text size="sm">CDR3: {tooltipState.cdr3Sequence}</Text>
        {tooltipState.legendLabel && (
          <Flex align="center" gap={5}>
            <Box w={10} h={10} bg={tooltipState.color} mt={-3} style={{ borderRadius: '50%' }} />
            <Text size="sm">{tooltipState.legendLabel}</Text>
          </Flex>
        )}
      </Box>
    );
  }, [tooltipState]);

  return (
    <Tooltip.Floating multiline position="bottom-end" label={tooltipContent} classNames={{ tooltip: 'help-tooltip' }}>
      <Box w={width} h={height} style={{ border: '0.5px solid #f0f0f0' }}>
        <svg width={width} height={height} style={{ display: 'block' }}>
          {clonotype.links.map((link) => {
            const a = layout[link.source];
            const b = layout[link.target];
            if (!a || !b) {
              return null;
            }
            return (
              <line
                key={`${link.source}-${link.target}`}
                x1={a.cx}
                y1={a.cy}
                x2={b.cx}
                y2={b.cy}
                stroke={linkColor}
                strokeOpacity={linkOpacity}
                strokeWidth={1}
              />
            );
          })}
          {clonotype.nodes.map((node) => {
            const pos = layout[node.id];
            if (!pos) {
              return null;
            }
            const nodeColor = getNodeColorForNode(node);
            return (
              <circle
                key={node.id}
                cx={pos.cx}
                cy={pos.cy}
                r={getNodeSize(node.cells.length)}
                fill={nodeColor}
                stroke="rgba(0,0,0,0.2)"
                strokeWidth={0.5}
                onMouseEnter={() =>
                  setTooltipState({
                    type: 'node',
                    clonotypeId: clonotype.clonotypeId,
                    numberOfCells: node.cells.length,
                    vgSegment: vGSegment,
                    cdrh3AaLen,
                    cdr3Sequence: node.cdr3Sequence,
                    color: getNodeColorForNode(node) ?? VIS_NEUTRAL_COLOR,
                    legendLabel: getLegendLabel(node),
                  })
                }
                onMouseLeave={() => setTooltipState(clonotypeTooltip)}
              />
            );
          })}
        </svg>
      </Box>
    </Tooltip.Floating>
  );
}
